import {
  OnInit, Input, Component, ChangeDetectorRef, ViewChildren, QueryList, ChangeDetectionStrategy
} from '@angular/core';
import {
  UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators, ValidatorFn, AbstractControl,
} from '@angular/forms';
import * as _ from 'lodash';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { BrandService } from 'shared/brand/brand.service';

import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { extendedInformationData } from '../../../segment-data/packages-segment-data';
import { ExtendedInformationModel } from '../../../models/extended-information.model';
import { BaseSegmentComponent } from '../../base/_base-segment.component';
import { segmentAnimationEmpty } from '../../base/animations';
import { contentData } from './table-data';


@Component({
  moduleId: module.id,
  selector: 'extended-information',
  templateUrl: 'extended-information.component.html',
  styleUrls: ['../../base/_base-segment.component.scss', './extended-information.component.scss'],
  animations: [segmentAnimationEmpty],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExtendedInformationComponent extends BaseSegmentComponent implements OnInit {
  contentData: any[] = contentData;

  // define in the Child
  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'extendedInformation'; // used in save request
  pathToScores: string = 'segments.extendedInformation.tallies';
  fieldNamesWithoutPrefix: boolean = true;

  segmentData: any = extendedInformationData;

  @Input() declare editMode: boolean;

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get itemDetailData(): Array<any> {
    return _.get(this.itemDetail, this.segmentName);
  }

  get scorecardFields(): any {
    return _.get(this.scorecardData, `segments.packages.fields`);
  }

  // returns segmentName where conditionals are placed
  get conditionalsSegmentName(): string {
    return 'packages';
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.segmentForm = this.createItemFG({});
    super.ngOnInit();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onControlPopulatedChange(populated: any, item: any) {
    super.onControlPopulatedChange(populated, item);
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  // replace parent method:
  protected setData(itemDetailData: any = {}): void {
    this.segmentForm = this.createItemFG(this.itemDetailData);
    this.showSegment();
    this.ref.markForCheck();
  }

  // replace parent method:
  protected getLookup(fieldName: string, segmentData: Array<SegmentFieldItem>): any {
    const field = this.getFieldByMap(fieldName, segmentData);
    return field && field.lookup ? field.lookup : null;
  }

  // replace parent method:
  protected createItemModel(item: any): ExtendedInformationModel {
    const model = new ExtendedInformationModel();
    // set other fields
    _.each(this.segmentData, (field: any) => {
      if (!field.field_map) { return; }

      const validators: any = this.getFieldValidators(field);
      const value = _.get(item, field.item_name, '');

      _.set(model, field.field_map, new UntypedFormControl(value, validators));
    });

    return model;
  }

  // redefine parent method
  protected getItemScore(item: any): any {
    return _.get(this.segmentScores, item.item_name);
  }

  // check whether the fiels is in score or not
  // field is from table-data
  protected hasItemInScores(field: any): boolean {
    const item = this.getFieldByMap(field.fieldName, this.segmentData);
    if (!item) { return false; }
    return _.has(this.segmentScores, item.item_name);
  }

  // redefine parent method
  protected handleScoreDetailsExpand(event: any): void {
    if (!this.editMode || _.isNil(event.item)) { return; }

    const item = event.item;
    const control = this.findControlByFieldMap(item.field_map);

    if (control) { control.focusControl(item.field_map); }
  }

  // replace this method - require with prefix 'packages'
  // redefine parent method
  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, (scorecardField: any) => {
      return (scorecardField.path.indexOf(`${this.segmentName}.${name}`) === 0);
    });
  }

  protected updateControlsValidation(): void {
    _.each(this.segmentData, (item: any) => {
      const control: AbstractControl = this.segmentForm.get(item.field_map);
      if (control) {
        this.updateControlValidators(control, this.getFieldValidators(item));
      }
    });
  }
}
